import React, { useCallback, useMemo, useState } from "react";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { displayPhaseForRow } from "../projectPhaseDisplay";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import { viewerCanReportExpansionProgress } from "./expansionInspectorAccess";
import ExpansionDailyProgressForm from "./ExpansionDailyProgressForm";
import {
  dismissExpansionDailyProgress,
  expansionDailyProgressDismissKey,
  isExpansionDailyProgressDismissed,
  submitExpansionDailyProgress,
} from "./expansionDailyProgressApi";
import { readExpansionEditDomains } from "./expansionProjectForms";
import { todayIsoDate } from "./expansionProgressUtils";
import "./workflowSchedule.css";

/** Daily prompt on expansion subsegment landing for inspectors to report contractor progress. */
export default function ReportedFootagePrompt({
  projectRow,
  acfDomains,
  apiUrl,
  getAuthHeaders,
  onSaved,
}) {
  const viewerEmail = getViewerEmailFromStorage();
  const domains = useMemo(
    () => readExpansionEditDomains(projectRow, acfDomains),
    [projectRow, acfDomains]
  );
  const cons = domains[COMMERCIAL_DOMAINS.CONSTRUCTION] || {};
  const inspector = cons.inspector;
  const lastReportedDate = cons.reported_footage_date ? String(cons.reported_footage_date).slice(0, 10) : "";
  const phase = displayPhaseForRow(projectRow);
  const phaseLower = String(phase || "").trim().toLowerCase();
  const inInstallPhase = phaseLower === "construction" || phaseLower === "fiber";
  const canPrompt = inInstallPhase && viewerCanReportExpansionProgress(viewerEmail, inspector);

  const dismissedKey = expansionDailyProgressDismissKey("project", viewerEmail, projectRow?.id);
  const [dismissed, setDismissed] = useState(() => isExpansionDailyProgressDismissed(dismissedKey));
  const [conduitFootage, setConduitFootage] = useState("");
  const [fiberFootage, setFiberFootage] = useState("");
  const [proofingFootage, setProofingFootage] = useState("");
  const [vaults, setVaults] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const needsEntry = canPrompt && !dismissed && lastReportedDate !== todayIsoDate();

  const saveReport = useCallback(async () => {
    if (!projectRow?.id) return;
    setBusy(true);
    setError("");
    try {
      await submitExpansionDailyProgress({
        projectRow,
        report: { conduitFootage, fiberFootage, proofingFootage, vaults, notes },
        apiUrl,
        getAuthHeaders,
      });
      dismissExpansionDailyProgress(dismissedKey);
      setDismissed(true);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not save daily progress.");
    } finally {
      setBusy(false);
    }
  }, [
    apiUrl,
    conduitFootage,
    dismissedKey,
    fiberFootage,
    getAuthHeaders,
    notes,
    onSaved,
    projectRow,
    proofingFootage,
    vaults,
  ]);

  if (!needsEntry) return null;

  return (
    <div className="phase-schedule-prompt commercial-pending-edits-banner" role="dialog" aria-label="Daily contractor progress">
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>Daily contractor progress</strong>
        <p className="project-detail-muted" style={{ margin: "4px 0 12px", fontSize: 13 }}>
          Report today&apos;s conduit footage, fiber footage, proofing completed, vaults placed, and
          progress notes for this subsegment.
        </p>
        <ExpansionDailyProgressForm
          idPrefix={`expansion-daily-${projectRow?.id}`}
          conduitFootage={conduitFootage}
          fiberFootage={fiberFootage}
          proofingFootage={proofingFootage}
          vaults={vaults}
          notes={notes}
          disabled={busy}
          onConduitFootageChange={setConduitFootage}
          onFiberFootageChange={setFiberFootage}
          onProofingFootageChange={setProofingFootage}
          onVaultsChange={setVaults}
          onNotesChange={setNotes}
        />
        {error ? (
          <p className="project-detail-error" style={{ marginTop: 8, fontSize: 13 }}>
            {error}
          </p>
        ) : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", alignSelf: "flex-start" }}>
        <button
          type="button"
          className="email-triggers-button email-triggers-button-primary"
          disabled={busy}
          onClick={() => void saveReport()}
        >
          {busy ? "Saving…" : "Submit report"}
        </button>
        <button
          type="button"
          className="email-triggers-button"
          disabled={busy}
          onClick={() => {
            dismissExpansionDailyProgress(dismissedKey);
            setDismissed(true);
          }}
        >
          Remind me later
        </button>
      </div>
    </div>
  );
}
